import { Link } from 'react-router-dom';
import { TRIAL_ENDED_COPY, TRIAL_ENDS_TODAY_LABEL, getTrialDeadlineCopy, getTrialDaysLeft, getTrialHoursLeft } from '@overlayqa/shared';
import { useBillingInfo } from '../../hooks/useBillingInfo';
import { useClerk } from '../../lib/auth';
import { LoadError } from '../shared/IssuesLoadError';

/**
 * Account → Billing reads the same team as /billing. Clerk's native page
 * reads personal subscriptions and cannot see the trial granted by
 * billing.service.ts:getTeamBillingInfo. Its original page remains available
 * for payment methods, statements, and subscription management. The account
 * menu hides its confirmed default-only plan list so it cannot contradict
 * the team trial shown here; real personal subscriptions remain manageable.
 */
export function AccountBillingPanel() {
  const { data: info, error, refetch } = useBillingInfo();
  const { closeUserProfile } = useClerk();

  if (!info) {
    return error
      ? <LoadError resource="billing information" error={error} onRetry={() => refetch()} />
      : <p role="status" className="text-sm text-gray-500">Loading billing information...</p>;
  }

  const expiresAt = info.betaExpiresAt ? new Date(info.betaExpiresAt) : null;
  // Live while the deadline is ahead. The count is the whole dates AFTER
  // today, so it reads 0 on the final date, which is still a trial day.
  const trialing = getTrialHoursLeft(info.betaExpiresAt) > 0;
  const daysLeft = getTrialDaysLeft(info.betaExpiresAt, info.trialTimeZone) ?? 0;

  return (
    <section aria-label="Workspace billing" className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Billing</h2>
        <p className="mt-1 text-sm text-gray-500">Your team's plan and usage.</p>
      </div>
      <div className="rounded-xl border border-gray-200 p-5">
        <h3 className="text-base font-semibold text-gray-900">
          {info.planName}{trialing ? ' trial' : ''}
        </h3>
        {trialing && expiresAt ? (
          <>
            <p className="mt-2 text-sm text-gray-700">Free trial · {daysLeft === 0 ? TRIAL_ENDS_TODAY_LABEL : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}</p>
            <p className="mt-1 text-sm text-gray-500">
              {info.trialTimeZone && info.betaExpiresAt
                ? getTrialDeadlineCopy(info.betaExpiresAt, info.trialTimeZone)
                : `Ends ${expiresAt.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}.`}
            </p>
          </>
        ) : (
          <p className="mt-2 text-sm text-gray-500">
            {info.plan ? 'Active subscription' : info.hadTrial ? TRIAL_ENDED_COPY : 'Choose a plan to start capturing.'}
          </p>
        )}
        <dl className="mt-5 grid grid-cols-2 gap-4 text-sm">
          <div><dt className="text-gray-500">Seats</dt><dd className="mt-1 text-gray-900">{info.currentSeats} / {info.maxSeats === Infinity ? '∞' : info.maxSeats} used</dd></div>
          <div><dt className="text-gray-500">Role</dt><dd className="mt-1 text-gray-900">{info.isOwner ? 'Team Owner' : 'Team Member'}</dd></div>
        </dl>
      </div>
      {!info.isOwner && <p className="text-sm text-gray-500">Contact your team owner to manage the subscription.</p>}
      <Link to="/billing" onClick={() => closeUserProfile()} className="btn-primary inline-flex">
        <span className="text-cap-trim">{info.isOwner ? 'Manage plan & seats' : 'View billing'}</span>
      </Link>
    </section>
  );
}
